import {
  DownloadLinkResponse,
  ListPersonalizedParams,
  PersonalizedDownloadLinkParams,
  PersonalizedStudyResponse,
  ResendStudyResult,
  toResendStudyResult,
} from 'helper/personalized';
import getProductKey from 'helper/requests/getProductKey';
import {
  ListedStudy, transformDownloadLink, transformListStudiesResponse, UploadStudiesParams,
} from '../../helper/studies';
import baseApi, { GenericResponse, rawBaseQuery } from './base';

interface GetDownloadLinkParams {
  id: number,
}

// `success` é do contrato publicado do kill-process (false = estudo já não estava rodando)
export type CancelStudyResponse = GenericResponse<null> & { success: boolean };

export const studiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listStudies: build.query<ListedStudy[], ListPersonalizedParams>({
      query: ({ ampereCases, page, pageSize }) => ({
        url: 'produtos/previvaz-arquivo/get-list',
        method: 'POST',
        body: {
          casos_ampere: ampereCases,
          page,
          page_size: pageSize,
        },
      }),
      providesTags: ['Study'],
      transformResponse: transformListStudiesResponse,
    }),
    getDownloadLink: build.query<string, GetDownloadLinkParams>({
      query: ({ id }) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-arquivo/get-download-link/?product_key=${productKey}`,
          method: 'POST',
          body: { caso_id: id },
        };
      },
      transformResponse: transformDownloadLink,
    }),
    uploadStudies: build.mutation<GenericResponse<PersonalizedStudyResponse>, UploadStudiesParams>({
      query: (params: UploadStudiesParams) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-arquivo/save-v2/?product_key=${productKey}`,
          method: 'POST',
          body: params,
        };
      },
      invalidatesTags: ['Study'],
    }),
    deleteStudy: build.mutation<GenericResponse<null>, number>({
      query: (id) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-arquivo/delete/?product_key=${productKey}`,
          method: 'DELETE',
          body: { id },
        };
      },
      invalidatesTags: ['Study'],
    }),
    cancelStudy: build.mutation<CancelStudyResponse, number>({
      query: (processId) => ({
        url: 'produtos/flux/kill-process/',
        method: 'POST',
        body: { process_id: processId },
      }),
      invalidatesTags: ['Study'],
    }),
    /** Link do zip de um estudo de Upload, como mutation (a tela nova pede ao clicar em Baixar). */
    uploadDownloadLink: build.mutation<DownloadLinkResponse, PersonalizedDownloadLinkParams>({
      query: (params) => {
        const productKey = getProductKey('prevs-personalizado');

        return {
          url: `produtos/previvaz-arquivo/get-download-link/?product_key=${productKey}`,
          method: 'POST',
          body: params,
        };
      },
    }),
    /**
     * Reenviar um estudo de Upload: a resposta do `re-execute` vem sem `code` (o mesmo tratamento
     * de `resendPersonalizedStudy`); `useReexecuteMutation` de `products/flux` fica para a
     * lista legada.
     */
    resendUploadStudy: build.mutation<ResendStudyResult, number>({
      queryFn: async (processId, api, extraOptions) => toResendStudyResult(await rawBaseQuery(
        { url: 'produtos/flux/re-execute', method: 'POST', body: { process_id: processId } },
        api,
        extraOptions,
      )),
      invalidatesTags: ['Study'],
    }),
  }),
});

export const {
  useListStudiesQuery,
  useGetDownloadLinkQuery,
  useUploadStudiesMutation,
  useDeleteStudyMutation,
  useCancelStudyMutation,
  useUploadDownloadLinkMutation,
  useResendUploadStudyMutation,
} = studiesApi;
